// THE FIELDS A FEED'S COLUMNS CAN BE MAPPED TO — ONE LIST, BECAUSE THE SECOND
// COPY HAD ALREADY DRIFTED.
//
// There are two column-review panels: one behind the file upload, one behind
// connect-by-URL. Each carried its own list, and on 2026-09-07 they disagreed:
//
//   propose-mapping.ts  MAPPING_FIELDS      9 fields   (the server, authoritative)
//   FeedUploadForm      FIELD_LABELS        9 fields
//   AddFeedByUrl        MAP_FIELDS          8 fields   — no `description`
//
// Driven side by side from the SAME inspection: the upload panel rendered six
// rows including Description, the URL panel rendered five and never showed it.
// The value still travelled — the whole `mapping` object is sent either way — so
// nothing was lost; what was missing was the publisher's ability to SEE what
// their description column had become, or to correct it.
//
// THAT MATTERS MORE ON THE URL PATH THAN ON THE OTHER ONE. `column_mapping` is
// written at creation (`createUrlBackedFeed`) and by an upload's `ON CONFLICT`
// clause. Re-uploading a file to the same label therefore replaces the mapping;
// nothing changes a URL feed's, because `updateFeedSync` does not touch that
// column and no endpoint does. So the review panel is the only moment a URL
// feed's mapping can ever be set, and one of its nine fields was not on it.
//
// Description is also the field that decides whether products match at all —
// matching is text over name and description, and `CatalogCoverage` counts
// `missedNoDescription` as its own miss reason.
//
// WHY A CLIENT COPY AT ALL. `propose-mapping.ts` imports `node:crypto` and the
// LLM client, so it cannot enter the CRA bundle — the same reason
// `FeedUploadForm`'s `MAX_FILE_SIZE` is a local const with a comment saying so.
// `column-map-shared.test.ts` reads the server file as text and fails if the two
// stop agreeing, which is the part a comment cannot do.

export interface FeedMapField {
  key: string;
  label: string;
  /** Refused by the server without one; the panel marks it with an asterisk. */
  required: boolean;
}

/**
 * In the server's own order, so a reader comparing the two lists is comparing
 * two lists rather than two orderings.
 */
export const FEED_MAP_FIELDS: readonly FeedMapField[] = [
  { key: 'id', label: 'Product ID', required: true },
  { key: 'name', label: 'Product name', required: true },
  { key: 'link', label: 'Affiliate link', required: true },
  { key: 'image', label: 'Image URL', required: true },
  { key: 'price', label: 'Price', required: false },
  { key: 'currency', label: 'Currency', required: false },
  { key: 'description', label: 'Description', required: false },
  { key: 'category', label: 'Category', required: false },
  { key: 'brand', label: 'Brand', required: false },
];

/**
 * Whether a field must be mapped before this feed can be accepted.
 *
 * 2Performant exports often carry no ID column — identity comes from the
 * affiliate link — so an unmapped id there is expected, not an error. Stated
 * here rather than in each panel, because it is the same rule on both and it
 * was already written twice.
 */
export function isRequiredFor(field: FeedMapField, network: string): boolean {
  return field.required && !(field.key === 'id' && network === '2performant');
}

/** The required fields still without a column, in the panels' render order. */
export function missingRequiredFields(
  mapping: Record<string, string>,
  network: string,
): FeedMapField[] {
  return FEED_MAP_FIELDS.filter(f => isRequiredFor(f, network) && !mapping[f.key]);
}
